import { Box, Typography, Stack } from "@mui/material";
import React, { memo, useCallback, useMemo } from "react";
import PayPeriodTable from "../../../../Components/PayrollEmployeeListTable";
import dayjs from "dayjs";
import { useFormikContext } from "formik";

const AttentionNeededEmployeesTable = ({
  setStep,
  setSelectedRow,
  accountOptions = [],
  rows = [],
  totals,
  setAddEmployeePopup,
  showProfitCenter = false,
}) => {
  // Read values directly from Formik context instead of prop-drilling
  const { values = {} } = useFormikContext() || {};

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    if (typeof dateStr === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr.trim())) {
      return dateStr.trim();
    }
    const d = dayjs(dateStr);
    return d.isValid() ? d.format("MM/DD/YYYY") : String(dateStr);
  }, []);

  const getAccountLabel = useCallback(() => {
    if (!values?.account) return "";
    const found = accountOptions?.find((opt) => opt.value === values.account);
    return found ? found.label : values.account;
  }, [values?.account, accountOptions]);

  const getFrequencyString = useCallback(() => {
    return `${formatDate(values?.payFrom)} - ${formatDate(values?.payTo)} (${values?.frequency || ""})`;
  }, [formatDate, values?.payFrom, values?.payTo, values?.frequency]);

  const columns = useMemo(
    () => [
      { label: "Employee Name", field: "name" },
      { label: "Department", field: "department" },
      { label: "Job Title", field: "jobTitle" },
      ...(showProfitCenter ? [{ label: "Profit Center", field: "profitCenter" }] : []),
      { label: "Hrs", field: "hrs" },
      { label: "Gross Pay", field: "grossPay" },
      { label: "Additions", field: "additions" },
      { label: "Deductions", field: "deductions" },
      { label: "EE Taxes", field: "eeTaxes" },
      { label: "Net Pay", field: "netPay" },
      { label: "ER Taxes", field: "erTaxes" },
      { label: "VIEW", field: "view" },
      { label: "", field: "voidStar" },
    ],
    [showProfitCenter]
  );

  const handleRowClick = useCallback(
    (row) => {
      const isSelectable =
        row.statusText === "Matched" ||
        row.statusText === "Unmapped" ||
        row.view === "Matched" ||
        row.view === "Unmapped" ||
        row.statusText === "Completed" ||
        row.view === "Completed";

      if (isSelectable) {
        const isMatched = row.statusText === "Matched" || row.view === "Matched" || row.statusText === "Completed" || row.view === "Completed";
        setSelectedRow?.({
          ...row,
          statusText: row.statusText || row.view || (isMatched ? "Matched" : "Unmapped"),
          view: row.view || row.statusText || (isMatched ? "Matched" : "Unmapped"),
          skipPayrollDetailsLoad: isMatched,
        });
        setStep?.(4);
      }
    },
    [setSelectedRow, setStep]
  );

  return (
    <Box height="100%" width="100%" display="flex" flexDirection="column">
      {/* Header Info */}
      <Box
        sx={{
          bgcolor: "#eaf5fc",
          borderRadius: 2,
          px: 1,
          py: 1,
          mb: 1,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <Typography variant="body2" fontSize={12} fontWeight={600} noWrap>
            Corporation Name: {values?.corporation?.corpName || values?.corporation}
          </Typography>
          <Typography variant="body2" fontSize={12} fontWeight={600} noWrap>
            Pay Frequency: {getFrequencyString()}
          </Typography>
          <Typography variant="body2" fontSize={12} fontWeight={600} noWrap>
            Pay Date: {formatDate(values?.payDate)}
          </Typography>
          <Typography variant="body2" fontSize={12} fontWeight={600} noWrap>
            Bank Account: {getAccountLabel()}
          </Typography>

          <Typography
            pr={2}
            fontWeight={700}
            color="#02aef1"
            fontSize={11}
            sx={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={(e) => setAddEmployeePopup?.(e.currentTarget)}
          >
            ADD EMPLOYEE
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <PayPeriodTable
          rows={rows}
          columns={columns}
          showTotals={true}
          customTotals={totals}
          isHoursDecimal={values?.IsHoursDecimal}
          onRowClick={handleRowClick}
        />
      </Box>
    </Box>
  );
};

export default memo(AttentionNeededEmployeesTable);
